/**
 * Animation utilities — Brand Book §1.7
 * Timing constants, prefers-reduced-motion utility
 * Skeleton → content fade transition helper
 */

/**
 * Motion timing constants — Brand Book §1.7 (IBM Carbon productive motion).
 * Single source of truth shared with the CSS `--motion-*` tokens in index.css
 * — keep the two in sync. Values are milliseconds. 500ms is the hard ceiling
 * for transitions/entrances; `skeleton` is an ambient-loop exception.
 */
export const TIMING = {
  micro: 70, //  micro-interaction (color/opacity tick)
  fade: 110, //  fades
  move: 150, //  small movement
  step: 240, //  step completion / resolve unit
  panel: 400, // panel resolve (ceiling 500)
  skeleton: 1500, // shimmer loop (ambient, ceiling-exempt)
  // Legacy aliases — existing callers; mapped onto the token scale.
  fast: 110, //   → fade  (was 200)
  layout: 240, // → step  (was 250)
  maxMicro: 300,
  progress: 240, // → step (was 500)
} as const;

/** CSS easing — the three Carbon curves (mirror of the `--ease-*` tokens). */
export const EASING = {
  std: 'var(--ease-std)', // standard — general motion
  in: 'var(--ease-in)', //   entrance — objects appearing
  out: 'var(--ease-out)', //  exit — objects leaving
  // Legacy keys.
  default: 'var(--ease-std)',
  inOut: 'var(--ease-std)',
} as const;

/**
 * Check if user prefers reduced motion.
 * Use this to conditionally disable animations.
 */
export function prefersReducedMotion(): boolean {
  // matchMedia is absent under SSR and in jsdom (test env) — fall back to "no
  // reduced motion" rather than throwing when it isn't implemented.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {return false;}
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get animation duration respecting prefers-reduced-motion.
 * Returns 0 if user prefers reduced motion (except for opacity transitions).
 */
export function getAnimationDuration(ms: number, isOpacity = false): number {
  if (prefersReducedMotion() && !isOpacity) {return 0;}
  return ms;
}

/**
 * Loading state tier helper — Brand Book §4.1
 * - Tier 1 (instant): <300ms → no indicator
 * - Tier 2 (brief): 300ms–3s → skeleton
 * - Tier 3 (extended): >3s → progress bar
 */
export type LoadingTier = 'instant' | 'skeleton' | 'progress';

export function getLoadingTier(expectedMs: number): LoadingTier {
  if (expectedMs < 300) {return 'instant';}
  if (expectedMs <= 3000) {return 'skeleton';}
  return 'progress';
}
